import { useEffect, useState } from "react";
import api from "@/api";
import { useLang } from "@/i18n";
import { ClientLayout } from "./ClientHome";
import { Sparkles, ChevronRight, Play } from "lucide-react";

const THEMES = ["rilassamento", "sonno", "respirazione", "preworkout"];

function embedFor(url) {
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/)|youtu\.be\/)([\w-]{6,})/);
  if (yt) return { type: "youtube", src: `https://www.youtube.com/embed/${yt[1]}` };
  const sp = url.match(/open\.spotify\.com\/(episode|track|show|playlist)\/([\w]+)/);
  if (sp) return { type: "spotify", src: `https://open.spotify.com/embed/${sp[1]}/${sp[2]}?utm_source=generator&theme=0` };
  if (/\.(mp3|wav|m4a|ogg)(\?|#|$)/i.test(url)) return { type: "audio", src: url };
  return { type: "link", src: url };
}

function Player({ url, title, testid }) {
  const emb = embedFor(url);
  if (emb.type === "youtube") {
    return <iframe data-testid={testid} src={emb.src} title={title}
      className="w-full rounded-xl border-0 aspect-video"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowFullScreen loading="lazy" />;
  }
  if (emb.type === "spotify") {
    return <iframe data-testid={testid} src={emb.src} title={title}
      className="w-full rounded-xl border-0" height="152"
      allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture" loading="lazy" />;
  }
  if (emb.type === "audio") {
    return <audio data-testid={testid} controls preload="none" src={emb.src} className="w-full" />;
  }
  return (
    <a data-testid={testid} href={url} target="_blank" rel="noopener noreferrer"
      className="kc-btn-ghost !py-2 text-sm inline-flex items-center gap-2">
      <Play className="w-4 h-4" /> Apri la traccia
    </a>
  );
}

export default function ClientMeditation() {
  const { t } = useLang();
  const [items, setItems] = useState(null);
  const [theme, setTheme] = useState(null);

  useEffect(() => {
    api.get("/client/meditations").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);

  if (items === null) return <ClientLayout><p className="text-muted-foreground">{t("common.loading")}</p></ClientLayout>;

  const shown = theme ? items.filter((m) => m.theme === theme) : items;

  return (
    <ClientLayout>
      <div data-testid="client-meditation-page" className="max-w-xl mx-auto w-full space-y-5">
        <h1 className="text-3xl font-black uppercase tracking-tight flex items-center gap-2">
          <Sparkles className="w-7 h-7 text-primary" /> {t("med.title")}
        </h1>
        <p className="text-sm text-muted-foreground -mt-2">{t("med.hint")}</p>
        <div className="flex gap-2 flex-wrap" data-testid="meditation-filters">
          <button data-testid="meditation-filter-all" onClick={() => setTheme(null)}
            className={`kc-badge !px-4 !py-2 cursor-pointer ${!theme ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"}`}>
            {t("med.all")}
          </button>
          {THEMES.map((th) => (
            <button key={th} data-testid={`meditation-filter-${th}`} onClick={() => setTheme(th)}
              className={`kc-badge !px-4 !py-2 cursor-pointer ${theme === th ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"}`}>
              {t(`med.${th}`)}
            </button>
          ))}
        </div>
        {shown.length === 0 && <p className="text-muted-foreground text-sm">{t("med.empty")}</p>}
        <div className="space-y-3" data-testid="meditation-list">
          {shown.map((m) => (
            <details key={m._id} className="kc-card px-5 py-4 group" data-testid={`meditation-${m._id}`}>
              <summary className="cursor-pointer list-none [&::-webkit-details-marker]:hidden flex items-center justify-between gap-3">
                <span className="min-w-0">
                  <span className="kc-badge bg-primary/12 text-primary mr-2">{t(`med.${m.theme}`)}</span>
                  <span className="font-bold">{m.title}</span>
                  <span className="text-xs text-muted-foreground ml-2">{m.duration_min} {t("med.minutes")}</span>
                </span>
                <ChevronRight className="w-4 h-4 text-primary shrink-0 transition-transform group-open:rotate-90" />
              </summary>
              <div className="mt-3">
                <Player url={m.url} title={m.title} testid={`meditation-player-${m._id}`} />
              </div>
            </details>
          ))}
        </div>
      </div>
    </ClientLayout>
  );
}
